import { BalanceRegistry } from '../../features/financialStatements/types'
import { MonthlyBudgetData } from '../types'

/**
 * Parses year and month from a dataKey timestamp
 * Format: YYYYMM00 (e.g., 20240100 = January 2024)
 * @param dataKey - The timestamp key
 * @returns Object with year and month
 */
export const parseDataKey = (dataKey: number): { year: number; month: number } => {
  const year = Math.floor(dataKey / 10000)
  const month = Math.floor((dataKey % 10000) / 100)
  return { year, month }
}

/**
 * Creates a dataKey timestamp from year and month
 * Format: YYYYMM00 (e.g., year: 2024, month: 1 → 20240100)
 * This is the inverse operation of parseDataKey
 * @param year - Year
 * @param month - Month (1-12)
 * @returns DataKey timestamp
 */
export const createDataKey = (year: number, month: number): number => {
  return year * 10000 + month * 100
}

/**
 * Checks if a balance registry has any elements data
 *
 * @param registry - Balance registry to check
 * @returns True if registry has elements with at least one entry
 *
 * @example
 * ```typescript
 * if (hasRegistryElements(row.newBudgets)) {
 *   // Process elements data
 * }
 * ```
 */
export const hasRegistryElements = (registry: BalanceRegistry | undefined): boolean => {
  return !!(registry?.elements && Object.keys(registry.elements).length > 0)
}

/**
 * Checks if a balance registry has any groups data
 *
 * @param registry - Balance registry to check
 * @returns True if registry has groups with at least one entry
 *
 * @example
 * ```typescript
 * if (hasRegistryGroups(row.newBudgets)) {
 *   // Process groups data
 * }
 * ```
 */
export const hasRegistryGroups = (registry: BalanceRegistry | undefined): boolean => {
  return !!(registry?.groups && Object.keys(registry.groups).length > 0)
}

/**
 * Checks if a balance registry has any data (elements or groups)
 *
 * @param registry - Balance registry to check
 * @returns True if registry has either elements or groups data
 *
 * @example
 * ```typescript
 * if (hasRegistryData(row.newBudgets)) {
 *   // Process registry data
 * }
 * ```
 */
export const hasRegistryData = (registry: BalanceRegistry | undefined): boolean => {
  return hasRegistryElements(registry) || hasRegistryGroups(registry)
}

/**
 * Extracts monthly budget data from a balance registry's elements
 *
 * @param registry - Balance registry containing elements (timestamp-based data)
 * @param createItem - Factory function to create the specific budget item type
 * @returns Array of monthly budget items
 *
 * @example
 * ```typescript
 * const budgets = extractMonthlyValuesFromRegistry(
 *   row.newBudgets,
 *   (year, month, value) => ({
 *     accountCode: 3000,
 *     year,
 *     month,
 *     value,
 *     dimensionId: null,
 *     dataType: StatementDataType.budget
 *   })
 * )
 * ```
 */
export const extractMonthlyValuesFromRegistry = <T extends MonthlyBudgetData>(
  registry: BalanceRegistry | undefined,
  createItem: (year: number, month: number, value: number | null) => T
): T[] => {
  if (!registry?.elements) return []

  return Object.entries(registry.elements).map(([dataKeyStr, value]) => {
    const dataKey = parseInt(dataKeyStr, 10)
    const { year, month } = parseDataKey(dataKey)
    return createItem(year, month, value)
  })
}
